import config from "../../config.json";

const BASE_AUTH_API = config.BASE_AUTH_API;
const ENDPOINT = "lud-dashboard";

const request = async ({
  token,
  endpoint = "",
  method = "GET",
  body,
  file,
  fields = {},
}) => {
  const headers = {
    Accept: "application/json",
    Authorization: `Bearer ${token}`,
  };

  let requestBody;
  if (file) {
    requestBody = new FormData();
    requestBody.append("file", file);
    Object.entries(fields || {}).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        requestBody.append(key, String(value));
      }
    });
  } else if (body !== undefined) {
    headers["Content-Type"] = "application/json";
    requestBody = JSON.stringify(body);
  }

  const response = await fetch(`${BASE_AUTH_API}${ENDPOINT}${endpoint}`, {
    method,
    headers,
    ...(requestBody !== undefined ? { body: requestBody } : {}),
  });

  const data = await response.json().catch(() => ({}));
  if (!response.ok || data.success === false) {
    const error = new Error(data.message || data.error || "Request failed");
    // Machine-readable cause (e.g. which working-hours day/shift is invalid).
    error.status = response.status;
    error.code = data.code;
    error.details = data.details;
    throw error;
  }
  return data;
};

// Password of one cashier credential (approved branch users only; the server
// decides). Never part of the normal dashboard responses.
export const revealCredentialAPI = (token, storeId, body) =>
  request({
    token,
    endpoint: `/credentials/${encodeURIComponent(storeId)}/reveal`,
    method: "POST",
    body,
  });

export const listDashboardStoresAPI = (token, ids) => {
  const params = new URLSearchParams();
  ids.forEach((id) => params.append("id", id));
  return request({ token, endpoint: `/stores?${params.toString()}` });
};

export const getDashboardOverviewAPI = (token, storeId) =>
  request({ token, endpoint: `/overview/${storeId}` });
export const updateDashboardPlatformLinksAPI = (token, platformLinks) =>
  request({
    token,
    endpoint: "/platform-links",
    method: "PATCH",
    body: { platformLinks },
  });
export const uploadDashboardBannerAPI = (token, storeId, file) =>
  request({ token, endpoint: `/banner/${storeId}`, method: "POST", file });
export const removeDashboardBannerAPI = (token, storeId) =>
  request({ token, endpoint: `/banner/${storeId}`, method: "DELETE" });
export const uploadBranchLicenseAPI = (token, storeId, branchId, file) =>
  request({
    token,
    endpoint: `/branches/${encodeURIComponent(storeId)}/${encodeURIComponent(branchId)}/license`,
    method: "POST",
    file,
  });
export const removeBranchLicenseAPI = (token, storeId, branchId) =>
  request({
    token,
    endpoint: `/branches/${encodeURIComponent(storeId)}/${encodeURIComponent(branchId)}/license`,
    method: "DELETE",
  });
export const getBranchLicenseUrlAPI = (token, storeId, branchId) =>
  request({
    token,
    endpoint: `/branches/${encodeURIComponent(storeId)}/${encodeURIComponent(branchId)}/license/url`,
  });
export const createDashboardRequestAPI = (token, body) =>
  request({ token, endpoint: "/requests", method: "POST", body });
export const reviewDashboardRequestAPI = (
  token,
  requestId,
  decision,
  note = "",
) =>
  request({
    token,
    endpoint: `/requests/${requestId}/review`,
    method: "PATCH",
    body: { decision, note },
  });
// The Excel of a menu e-mail request (what was / will be sent) as a Blob; the server checks access.
export const downloadMenuEmailAttachmentAPI = async (token, requestId, index = 0) => {
  const response = await fetch(`${BASE_AUTH_API}${ENDPOINT}/requests/${encodeURIComponent(requestId)}/email-attachment/${index}`, { headers: { Authorization: `Bearer ${token}` } });
  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    const error = new Error(data.message || "Request failed");
    error.status = response.status;
    throw error;
  }
  return response.blob();
};
export const cancelDashboardRequestAPI = (token, requestId, note = "") =>
  request({
    token,
    endpoint: `/requests/${requestId}/cancel`,
    method: "PATCH",
    body: { note },
  });
export const uploadDashboardRequestFileAPI = (token, requestId, kind, file) =>
  request({
    token,
    endpoint: `/requests/${requestId}/files/${kind}`,
    method: "POST",
    file,
  });
export const getDashboardRequestFileUrlAPI = (token, requestId, fileId) =>
  request({ token, endpoint: `/requests/${requestId}/files/${fileId}/url` });
export const executeDashboardActionAPI = (token, body) =>
  request({ token, endpoint: "/actions/execute", method: "POST", body });

export const listDashboardNotesAPI = (token, storeId) =>
  request({
    token,
    endpoint: `/accountant-notes?storeId=${encodeURIComponent(storeId)}`,
  });
export const createDashboardNoteAPI = (token, body) =>
  request({ token, endpoint: "/accountant-notes", method: "POST", body });
export const deleteDashboardNoteAPI = (token, id) =>
  request({ token, endpoint: `/accountant-notes/${id}`, method: "DELETE" });

export const updateDashboardCompletionItemAPI = (
  token,
  storeId,
  itemId,
  data = null,
) =>
  request({
    token,
    endpoint: `/completion/${storeId}/${itemId}`,
    method: "PATCH",
    body: { data },
  });
export const listDashboardNotificationsAPI = (token, params = {}) => {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "")
      query.set(key, value);
  });
  return request({
    token,
    endpoint: `/notifications${query.toString() ? `?${query}` : ""}`,
  });
};

// Signed, ready-to-use logo URL of a store (`url` is null when it has none).
export const getStoreLogoUrlAPI = (token, storeId) =>
  request({
    token,
    endpoint: `/logo/${encodeURIComponent(storeId)}/url`,
  });
